import { formatCurrency } from 'common/FormatUtilities';
import { CuisineListing } from 'components/cards/cuisine-listing';
import { DishCardRating } from 'components/cards/dish-card-rating';
import { DynamicImage } from 'components/dynamic-image';
import { WavyEdge } from 'components/flair/WavyEdge';
import React from 'react';
import { NoFollowLink } from 'shared/no-follow-link';
import { getFrontEndUrl } from 'utils/environment-util';
import { CityPageDishFragment } from 'utils/gqlReactTypings.generated.d';

const IMAGE_WIDTH = 238;
const IMAGE_HEIGHT = 160;
const IMAGE_HEIGHT_CLASS = `h-[160px]`;

interface IProps {
  className?: string;
  dish: CityPageDishFragment;
}

export const DishCard: React.FC<IProps> = ({ className, dish }) => {
  const href = `${getFrontEndUrl()}/order/shef/${dish.userId}/${dish.slug ?? ''}`;

  // Just type-safety, this should not happen
  if (!dish?.imageUrl || !dish?.name) return null;

  return (
    <NoFollowLink
      href={href}
      key={dish.id}
      className={`flex h-[368px] w-60 cursor-pointer flex-col
                  items-start rounded-[8px] border border-neutral-50 bg-white ${className}`}>
      {/* Image Header */}
      <div className={`relative ${IMAGE_HEIGHT_CLASS}`}>
        <DynamicImage
          className='rounded-t-[7px]' // Just needs to be a _little_ less rounded
          src={dish.imageUrl}
          alt={dish.name}
          width={IMAGE_WIDTH}
          height={IMAGE_HEIGHT}
          sourceWidth={IMAGE_WIDTH}
          sourceHeight={IMAGE_HEIGHT}
          effect={'opacity'}
        />
        <div className='absolute left-2 top-2 rounded bg-strawberry-50 px-2'>
          <span className='text-xs font-semibold text-neutral-800'>Popular</span>
        </div>
        <WavyEdge />
      </div>

      {/* Dish Body */}
      <div className='flex w-full grow flex-col px-3 pb-0 pt-1.5 text-sm font-medium'>
        <CuisineListing className='mb-0.5' formattedCuisineCategories={dish.shef.formattedCuisineCategories} />
        <span className='mb-1 line-clamp-2 text-lg font-bold leading-6 text-neutral-800'>{dish.name}</span>
        <span className='mb-2 text-sm font-semibold leading-none text-neutral-600'>{dish.portion}</span>
        <DishCardRating className='mb-2' dish={dish} />
        <span className='text-sm font-semibold leading-none text-neutral-600'>{`Shef ${dish.shef?.publicName}`}</span>
      </div>

      {/* Dish Footer */}
      <div className='flex w-full flex-col p-3 pt-0'>
        <div className='mb-2 grow border-t border-neutral-100'></div>
        <div className='flex flex-row items-center justify-between'>
          <span className='text-lg font-medium leading-6 text-neutral-800'>{formatCurrency(dish.price)}</span>
          <i className='far fa-plus text-xl leading-6 text-radish-200' />
        </div>
      </div>
    </NoFollowLink>
  );
};
